import React from "react";
import { motion } from "framer-motion";
import siteData from "../siteData";

const fadeUp = {
  hidden: { opacity: 0, y: 40, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1] },
  },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const slideIn = {
  hidden: { opacity: 0, x: -20, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { duration: 0.6, ease: [0.19, 1, 0.22, 1] },
  },
};

const pillStagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const pillAnim = {
  hidden: { opacity: 0, scale: 0.8, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.19, 1, 0.22, 1] },
  },
};

const vp = { once: true, amount: 0.15 };

function getSkillLevel(percent) {
  if (percent >= 85) return "skill-expert";
  if (percent >= 65) return "skill-advanced";
  return "skill-intermediate";
}

const categoryLabels = {
  languages: "Languages",
  frontend: "Frontend",
  backend: "Backend",
  devops: "DevOps & Cloud",
};

export default function Resume() {
  const grouped = siteData.skills.reduce((acc, skill) => {
    const cat = skill.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(skill);
    return acc;
  }, {});

  const year = new Date().getFullYear();
  const experienceYears = year - 2021;

  return (
    <section className="resume section" id="resume">
      <motion.div
        className="container"
        initial="hidden"
        whileInView="visible"
        viewport={vp}
        variants={stagger}
      >
        {/* Watermark */}
        <div className="section-watermark">RESUME</div>

        <div className="section-header">
          <motion.div variants={fadeUp}>
            <span className="section-tag">Resume</span>
          </motion.div>
          <motion.h2 className="section-title" variants={fadeUp}>
            Experience & Skills
          </motion.h2>
          <motion.p className="section-subtitle" variants={fadeUp}>
            {experienceYears}+ years building scalable full-stack applications
            at industry-leading companies.
          </motion.p>
        </div>

        <div className="resume-grid">
          {/* Left Column - Experience */}
          <motion.div className="resume-column" variants={fadeUp}>
            <h3>
              <span className="icon">
                <i className="fa fa-briefcase" />
              </span>
              Work Experience
            </h3>
            <div className="timeline">
              {siteData.jobs.map((job, i) => (
                <motion.div
                  key={i}
                  className="timeline-item"
                  variants={slideIn}
                  whileHover={{ x: 4, borderColor: "rgba(0,230,138,0.15)" }}
                  transition={{ duration: 0.3 }}
                >
                  <h4>{job.company}</h4>
                  <div className="role">{job.role}</div>
                  <div className="date">{job.date}</div>
                  <p>{job.desc}</p>
                </motion.div>
              ))}
            </div>

            <h3 style={{ marginTop: "2.5rem" }}>
              <span className="icon">
                <i className="fa fa-heart" />
              </span>
              Volunteer
            </h3>
            <div className="timeline">
              <motion.div
                className="timeline-item"
                variants={slideIn}
                whileHover={{ x: 4, borderColor: "rgba(0,230,138,0.15)" }}
                transition={{ duration: 0.3 }}
              >
                <h4>{siteData.volunteer.company}</h4>
                <div className="role">{siteData.volunteer.role}</div>
                <div className="date">{siteData.volunteer.date}</div>
                <p>{siteData.volunteer.desc}</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Skills + Education */}
          <motion.div className="resume-column" variants={fadeUp}>
            <h3>
              <span className="icon">
                <i className="fa fa-code" />
              </span>
              Skills
            </h3>
            {Object.entries(grouped).map(([cat, skills]) => (
              <div key={cat} className="skill-category">
                <div className="skill-category-label">
                  {categoryLabels[cat] || cat}
                </div>
                <motion.div
                  className="skills-container"
                  variants={pillStagger}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  {skills.map((skill, i) => (
                    <motion.div
                      key={i}
                      className={`skill-pill ${getSkillLevel(skill.percent)}`}
                      variants={pillAnim}
                      whileHover={{
                        y: -2,
                        boxShadow: "0 0 30px rgba(0,230,138,0.12)",
                      }}
                    >
                      <span className="skill-dot" />
                      {skill.name}
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            ))}

            <h3 style={{ marginTop: "2.5rem" }}>
              <span className="icon">
                <i className="fa fa-book" />
              </span>
              Education
            </h3>
            <motion.div className="education-card" variants={slideIn}>
              <h4>{siteData.education.school}</h4>
              <div className="degree">{siteData.education.degree}</div>
              <div className="date">{siteData.education.graduationDate}</div>
              <p>{siteData.education.details}</p>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
